import { removeDiacritics } from '#src/app/shared/utils/string';

import type { DecisionMakerHitData, DecisionMakerMatch, LinkableDecisionMakerHitData } from './types';

export type DecisionMakerMatchRules = {
	// longer queries are topics ("stop the new highway through our town"), not names
	maxQueryWords: number;
	fullName: {
		// petitions needed relative to the next decision maker with the same name
		minLeadRatio: number;
	};
	surnameOnly: {
		minPetitions: number;
		requireVerified: boolean;
		// petitions needed relative to the next decision maker with that surname
		minLeadRatio: number;
	};
};

const NAME_SUFFIXES = new Set(['jr', 'sr', 'ii', 'iii', 'iv']);

/**
 * Lowercase words without accents, punctuation or a trailing suffix: "Robert F. Kennedy Jr." gives robert, f, kennedy
 */
function toWords(value: string): string[] {
	const words = removeDiacritics(value.toLowerCase())
		.replace(/[^\p{L}\p{N}\s]/gu, '')
		.split(/\s+/)
		.filter(Boolean);

	while (words.length > 1 && NAME_SUFFIXES.has(words.at(-1) ?? '')) words.pop();
	return words;
}

/**
 * The first and last word with any middle words that aren't initials: "Donald J Trump" and "donald trump" compare equal
 */
function toNameKey(words: string[]): string {
	if (words.length < 2) return words.join(' ');
	const middleWords = words.slice(1, -1).filter((w) => w.length > 1);
	return [words[0], ...middleWords, words.at(-1)].join(' ');
}

function byPetitionsDesc(a: DecisionMakerHitData, b: DecisionMakerHitData): number {
	return b.petition_count - a.petition_count;
}

function isLinkable(dm: DecisionMakerHitData): dm is LinkableDecisionMakerHitData {
	return !!dm.slug;
}

/**
 * The most petitioned candidate, if it has at least `minLeadRatio` times the petitions of the next one
 */
function findClearLeader(candidates: DecisionMakerHitData[], minLeadRatio: number): DecisionMakerHitData | undefined {
	const sorted = [...candidates].sort(byPetitionsDesc);
	const top = sorted.at(0);
	const next = sorted.at(1);
	if (!top) return undefined;
	if (next && top.petition_count < next.petition_count * minLeadRatio) return undefined;
	return top;
}

/**
 * Several people can share a full name ("john smith"): a verified one wins, otherwise one must clearly lead
 */
function matchFullName(
	queryWords: string[],
	candidates: DecisionMakerHitData[],
	{ fullName }: DecisionMakerMatchRules,
): DecisionMakerHitData | undefined {
	const queryKey = toNameKey(queryWords);
	const namesakes = candidates.filter((dm) => toNameKey(toWords(dm.name)) === queryKey);
	const verified = namesakes.filter((dm) => dm.verified_at);

	return findClearLeader(verified.length ? verified : namesakes, fullName.minLeadRatio);
}

/**
 * A surname alone is ambiguous, so it only names someone well known who clearly stands out among namesakes
 */
function matchSurname(
	surname: string,
	candidates: DecisionMakerHitData[],
	{ surnameOnly }: DecisionMakerMatchRules,
): DecisionMakerHitData | undefined {
	const top = findClearLeader(
		candidates.filter((dm) => toWords(dm.name).at(-1) === surname),
		surnameOnly.minLeadRatio,
	);

	if (!top) return undefined;
	if (surnameOnly.requireVerified && !top.verified_at) return undefined;
	if (top.petition_count < surnameOnly.minPetitions) return undefined;
	return top;
}

/**
 * The query as sent to Typesense, which requires every word: without middle initials or a suffix, so
 * "donald j trump" still finds a "Donald Trump" record (the matcher ignores them on the record's side too)
 */
export function toDecisionMakerLookupQuery(query: string): string {
	return toNameKey(toWords(query));
}

/**
 * Whether a query could name a decision maker at all, so the lookup can be skipped otherwise
 */
export function isDecisionMakerQuery(query: string, { maxQueryWords }: DecisionMakerMatchRules): boolean {
	const wordCount = toWords(query).length;
	return wordCount > 0 && wordCount <= maxQueryWords;
}

/**
 * The decision maker the query names among the search candidates, if any.
 * Namesakes without a profile still count against a lead, but only a linkable winner is returned.
 */
export function matchDecisionMaker(
	query: string,
	candidates: DecisionMakerHitData[],
	rules: DecisionMakerMatchRules,
): DecisionMakerMatch | null {
	if (!isDecisionMakerQuery(query, rules)) return null;

	const queryWords = toWords(query);
	const matchType = queryWords.length === 1 ? 'surname' : 'name';
	const match =
		matchType === 'surname'
			? matchSurname(queryWords[0], candidates, rules)
			: matchFullName(queryWords, candidates, rules);

	return match && isLinkable(match) ? { decisionMaker: match, matchType } : null;
}
